import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { GexRayWidget } from '../components/GexRayWidget';
import { isQfConnected, onQfConnectionChange } from '../services/qfClient';
import { isBeforeMarketOpen, isWeekend } from '../utils/marketDate';

// Fallback polling when WebSocket is disconnected during market hours
const FALLBACK_POLL_INTERVAL_MS = 30_000;

function useIsMarketOpen(): boolean {
  // Simple check: weekday between 9:30 AM and 4:00 PM ET
  const check = useCallback(() => {
    if (isWeekend()) return false;
    if (isBeforeMarketOpen()) return false;
    // Check if after 4:00 PM ET
    const now = new Date();
    const etString = now.toLocaleString('en-US', { timeZone: 'America/New_York' });
    const et = new Date(etString);
    if (et.getHours() >= 16) return false;
    return true;
  }, []);

  // Re-check every minute
  const ref = useRef(check());
  useEffect(() => {
    const interval = setInterval(() => {
      ref.current = check();
    }, 60_000);
    return () => clearInterval(interval);
  }, [check]);

  return ref.current;
}

export function GexRayPopoutPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const marketOpen = useIsMarketOpen();
  // Live GEX is QF Centrifugo, not the portal WS. Poll REST while QF is down.
  const [qfUp, setQfUp] = useState(() => isQfConnected());
  useEffect(() => onQfConnectionChange(setQfUp), []);

  // Set document title with connection status
  useEffect(() => {
    const wsStatus = qfUp ? 'WS' : 'REST';
    const marketStatus = marketOpen ? 'LIVE' : 'CLOSED';
    document.title = `GEX-Ray [${marketStatus}] [${wsStatus}]`;
  }, [qfUp, marketOpen]);

  // Fallback polling: 30s when QF is down during market hours
  useEffect(() => {
    if (qfUp || !marketOpen) return;

    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['gexRayData'] });
    }, FALLBACK_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [queryClient, qfUp, marketOpen]);

  return (
    <div className="h-screen w-screen bg-dark-bg flex flex-col">
      <div className="flex-1 min-h-0">
        <GexRayWidget windowId="popout-1" onRemove={() => {}} />
      </div>
      <div className="flex-1 min-h-0">
        <GexRayWidget windowId="popout-2" onRemove={() => {}} />
      </div>
    </div>
  );
}
